import { StatusSwitch } from '@app/modules/admin/components/permission/StatusSwitch';
import {
  useUsersAuthzAPIAdminPermissionsWriteAllowPermissionToUser,
  useUsersAuthzAPIAdminPermissionsWriteDenyPermissionToUser,
  useUsersAuthzAPIAdminPermissionsWriteResetUserPermission,
  type UserPermissionDto,
} from '@mi-front/api-clients';
import { Box, Card, CardContent, Checkbox, FormControlLabel } from '@mui/material';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import type { PermissionItemComponentProps } from './ActionPermissionsView';
import { useLocalizedText } from './GroupPermissionItem';

interface Props extends PermissionItemComponentProps<UserPermissionDto> {
  readonly userId: string;
  readonly groupId: string | null;
}

export function UserPermissionItem(p: Props) {
  const { $t } = useIntl();
  const { userId, groupId, permissionState: initialUserPermission, permissionDefinition } = p;

  const [userPermission, setUserPermission] = useState(initialUserPermission);

  const mutationParams = {
    userId,
    permission: userPermission.permission,
  };

  const allowMutation = useUsersAuthzAPIAdminPermissionsWriteAllowPermissionToUser({
    mutation: {
      onSuccess: (data) => {
        setUserPermission(data as UserPermissionDto);
      },
    },
  });

  const denyMutation = useUsersAuthzAPIAdminPermissionsWriteDenyPermissionToUser({
    mutation: {
      onSuccess: (data) => {
        setUserPermission(data as UserPermissionDto);
      },
    },
  });

  const resetMutation = useUsersAuthzAPIAdminPermissionsWriteResetUserPermission({
    mutation: {
      onSuccess: (data) => {
        setUserPermission(data as UserPermissionDto);
      },
    },
  });

  const isUpdating = allowMutation.isPending || denyMutation.isPending || resetMutation.isPending;

  const canInheritFromGroup = !!groupId;
  const inheritsFromGroup = !!userPermission.groupId;

  const onInheritFromGroupChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inherit = event.target.checked;

    if (inherit) {
      resetMutation.mutate({ userId, permission: userPermission.permission });
    } else {
      userPermission.allowed
        ? denyMutation.mutate(mutationParams)
        : allowMutation.mutate(mutationParams);
    }
  };

  const disablePermissionToggle = isUpdating || inheritsFromGroup;

  return (
    <Card>
      <CardContent>
        <Box sx={{ fontWeight: '500' }}>{useLocalizedText(permissionDefinition.description)}</Box>

        {canInheritFromGroup && (
          <Box display="flex" alignItems="center">
            <FormControlLabel
              sx={{ marginBottom: 0 }}
              control={
                <Checkbox
                  size="small"
                  checked={inheritsFromGroup}
                  onChange={onInheritFromGroupChange}
                  disabled={isUpdating}
                  sx={{ paddingRight: 0.5 }}
                />
              }
              label={
                <span style={{ fontSize: '14px' }}>
                  {$t({
                    id: 'userPermission.inheritsFromGroup',
                    defaultMessage: 'Hereda de grupo',
                  })}
                </span>
              }
            />
            {inheritsFromGroup && (
              <StatusSwitch
                checked={userPermission.allowed}
                disabled
                sx={{ marginBottom: 0 }}
                onChange={() => {
                  // noop
                }}
                size="small"
              />
            )}
          </Box>
        )}

        {!inheritsFromGroup && (
          <Box display="flex" alignItems="center" gap={2} mt={1}>
            <StatusSwitch
              checked={userPermission.allowed}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                event.target.checked
                  ? allowMutation.mutate(mutationParams)
                  : denyMutation.mutate(mutationParams);
              }}
              disabled={disablePermissionToggle}
              sx={{ marginBottom: 0 }}
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
